import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Plus, Trash2, Clock, Save, Loader2, CalendarDays
} from "lucide-react";
import { API } from "@/App";

const timeSlots = [];
for (let h = 8; h <= 18; h++) {
  timeSlots.push(`${h.toString().padStart(2, "0")}:00`);
  if (h < 18) timeSlots.push(`${h.toString().padStart(2, "0")}:30`);
}

export const ContractorAvailability = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [selectedDates, setSelectedDates] = useState([]);
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState({ start: "09:00", end: "17:00", type: "standard" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectionMode, setSelectionMode] = useState("single"); // "single" or "multiple"

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/contractors/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(response.data.slots || []);
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const getSlotsForDate = (date) => {
    const dateStr = formatDate(date);
    return slots.filter(s => s.date === dateStr);
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    
    if (selectionMode === "single") {
      setSelectedDates([date]);
    } else {
      // Multiple selection mode
      const dateStr = formatDate(date);
      const isSelected = selectedDates.some(d => formatDate(d) === dateStr);
      
      if (isSelected) {
        setSelectedDates(selectedDates.filter(d => formatDate(d) !== dateStr));
      } else {
        setSelectedDates([...selectedDates, date]);
      }
    }
  };

  const addSlotToSelectedDates = () => {
    if (selectedDates.length === 0) {
      toast.error("Please select at least one date");
      return;
    }

    const newSlots = selectedDates.map(date => ({
      date: formatDate(date),
      start_time: newSlot.start,
      end_time: newSlot.end,
      slot_type: newSlot.type,
      temp_id: `${Date.now()}_${formatDate(date)}`
    }));

    // Filter out duplicates
    const existingKeys = slots.map(s => `${s.date}_${s.start_time}_${s.end_time}`);
    const uniqueNewSlots = newSlots.filter(ns => 
      !existingKeys.includes(`${ns.date}_${ns.start_time}_${ns.end_time}`)
    );

    if (uniqueNewSlots.length < newSlots.length) {
      toast.info(`${newSlots.length - uniqueNewSlots.length} duplicate slot(s) skipped`);
    }

    setSlots([...slots, ...uniqueNewSlots]);
    toast.success(`Added availability for ${uniqueNewSlots.length} date(s)`);
  };

  const removeSlot = (slotId) => {
    setSlots(slots.filter(s => s.slot_id !== slotId && s.temp_id !== slotId));
  };

  const clearSelectedDates = () => {
    setSelectedDates([]);
  };

  const selectWeekdays = () => {
    // Select next 5 weekdays
    const dates = [];
    let current = new Date();
    current.setHours(0, 0, 0, 0);
    
    while (dates.length < 5) {
      current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
      const day = current.getDay();
      if (day !== 0 && day !== 6) { // Not weekend
        dates.push(new Date(current));
      }
    }
    setSelectedDates(dates);
    toast.success("Selected next 5 weekdays");
  };

  const selectNextWeek = () => {
    // Select all days next week
    const dates = [];
    let current = new Date();
    current.setHours(0, 0, 0, 0);
    
    // Move to next Monday
    const daysUntilMonday = (8 - current.getDay()) % 7 || 7;
    current = new Date(current.getTime() + daysUntilMonday * 24 * 60 * 60 * 1000);
    
    for (let i = 0; i < 7; i++) {
      dates.push(new Date(current.getTime() + i * 24 * 60 * 60 * 1000));
    }
    setSelectedDates(dates);
    toast.success("Selected next week (Mon-Sun)");
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      // Get all unique dates from slots
      const uniqueDates = [...new Set(slots.map(s => s.date))];
      
      // Save slots for all dates
      await axios.post(
        `${API}/contractors/availability`,
        slots.map(s => ({
          date: s.date,
          start_time: s.start_time,
          end_time: s.end_time,
          slot_type: s.slot_type
        })),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Availability saved for ${uniqueDates.length} date(s)!`);
      fetchAvailability();
    } catch (error) {
      toast.error("Failed to save availability");
    } finally {
      setSaving(false);
    }
  };

  // Get slots for all selected dates
  const selectedDateSlots = selectedDates.length === 1 
    ? getSlotsForDate(selectedDates[0])
    : [];

  // Mark dates with slots
  const datesWithSlots = [...new Set(slots.map(s => s.date))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/contractor")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-semibold font-outfit">Manage Availability</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Calendar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Select Dates</span>
                <div className="flex gap-2">
                  <Button
                    variant={selectionMode === "single" ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSelectionMode("single");
                      setSelectedDates(selectedDates.slice(0, 1));
                    }}
                  >
                    Single
                  </Button>
                  <Button
                    variant={selectionMode === "multiple" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectionMode("multiple")}
                  >
                    Multiple
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Calendar
                mode={selectionMode === "multiple" ? "multiple" : "single"}
                selected={selectionMode === "multiple" ? selectedDates : selectedDates[0]}
                onSelect={selectionMode === "multiple" 
                  ? (dates) => setSelectedDates(dates || [])
                  : (date) => handleDateSelect(date)
                }
                className="rounded-md border"
                modifiers={{
                  hasSlot: (date) => datesWithSlots.includes(formatDate(date))
                }}
                modifiersStyles={{
                  hasSlot: { backgroundColor: "#dcfce7", borderRadius: "100%" }
                }}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              />

              {/* Quick selection buttons */}
              {selectionMode === "multiple" && (
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Quick select:</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={selectWeekdays}>
                      <CalendarDays className="w-4 h-4 mr-1" />
                      Next 5 Weekdays
                    </Button>
                    <Button variant="outline" size="sm" onClick={selectNextWeek}>
                      <CalendarDays className="w-4 h-4 mr-1" />
                      Next Full Week
                    </Button>
                    {selectedDates.length > 0 && (
                      <Button variant="outline" size="sm" onClick={clearSelectedDates}>
                        Clear ({selectedDates.length})
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {selectedDates.length > 0 && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-blue-700">
                    {selectedDates.length} date{selectedDates.length > 1 ? 's' : ''} selected
                  </p>
                  {selectedDates.length <= 5 && (
                    <p className="text-xs text-blue-600 mt-1">
                      {selectedDates.map(d => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })).join(', ')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Slots management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Set Time Slots</span>
                <Badge variant="secondary">{slots.length} total slots</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new slot */}
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">Add availability</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Start Time</Label>
                    <Select
                      value={newSlot.start}
                      onValueChange={(v) => setNewSlot({ ...newSlot, start: v })}
                    >
                      <SelectTrigger data-testid="start-time-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">End Time</Label>
                    <Select
                      value={newSlot.end}
                      onValueChange={(v) => setNewSlot({ ...newSlot, end: v })}
                    >
                      <SelectTrigger data-testid="end-time-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="emergency"
                      checked={newSlot.type === "emergency"}
                      onCheckedChange={(c) => setNewSlot({ ...newSlot, type: c ? "emergency" : "standard" })}
                    />
                    <Label htmlFor="emergency" className="text-sm">Available for emergencies</Label>
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={addSlotToSelectedDates}
                  disabled={selectedDates.length === 0}
                  data-testid="add-slot-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add to {selectedDates.length || 0} Selected Date{selectedDates.length !== 1 ? 's' : ''}
                </Button>
              </div>

              {/* Existing slots for single date view */}
              {selectionMode === "single" && selectedDates.length === 1 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    Slots for {selectedDates[0].toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}:
                  </p>
                  {selectedDateSlots.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No availability set for this date
                    </p>
                  ) : (
                    selectedDateSlots.map((slot) => (
                      <div
                        key={slot.slot_id || slot.temp_id}
                        className="flex items-center justify-between p-3 bg-white border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">
                            {slot.start_time} - {slot.end_time}
                          </span>
                          <Badge variant={slot.slot_type === "emergency" ? "destructive" : "secondary"}>
                            {slot.slot_type}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSlot(slot.slot_id || slot.temp_id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Summary of all slots */}
              {slots.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">All scheduled availability:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {[...new Set(slots.map(s => s.date))].sort().map(date => {
                      const dateSlots = slots.filter(s => s.date === date);
                      const dateObj = new Date(date);
                      return (
                        <div key={date} className="text-sm flex items-center justify-between py-1">
                          <span className="text-muted-foreground">
                            {dateObj.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </span>
                          <span className="font-medium">{dateSlots.length} slot{dateSlots.length > 1 ? 's' : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Save button */}
              <Button
                className="w-full"
                onClick={saveAvailability}
                disabled={saving || slots.length === 0}
                data-testid="save-availability-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save All Availability
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
